import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { toast } from '@grandvisionhq/common';
import { useLazyQuery } from '@grandvisionhq/graphql';
import { useIntl, useApiConfig, useFeatureToggles } from '@grandvisionhq/state';
import { dispatchTrackErrorEvent } from '@grandvisionhq/svc-tealium/events';
import { isNil } from '@grandvisionhq/utils';
import { useCartSettings } from '../context/CartSettings/context.js';
import { toCartView } from '../mappers/view/to-cart-view.js';
import '../models/add-to-cart/add-to-cart.js';
import '../models/cart/cart.js';
import { CheckoutState } from '../models/checkoutState/checkoutState.js';
import '../operations/fragments/cart-details.js';
import '../operations/fragments/cart-validation-errors.js';
import '../operations/fragments/recover-inactive-cart-failure.js';
import '../operations/fragments/recover-inactive-cart-result.js';
import '../operations/mutations/add-cart-line-items.js';
import '../operations/mutations/add-cart-voucher-codes.js';
import '../operations/mutations/create-cart.js';
import '../operations/mutations/create-cart-from-instant-checkout-link.js';
import '../operations/mutations/create-cart-from-order.js';
import '../operations/mutations/duplicate-cart-line-items.js';
import '../operations/mutations/remove-cart-voucher-codes.js';
import '../operations/mutations/remove-cart-line-items.js';
import '../operations/mutations/update-cart-line-items.js';
import '../operations/mutations/recover-inactive-cart.js';
import { GET_CART } from '../operations/queries/get-cart.js';
import '../operations/queries/get-total-line-items.js';
import '../operations/queries/get-cart-estimated-delivery.js';
import '../utils/cart-count-var.js';
import '../utils/cart-id-var.js';
import '@grandvisionhq/commercetools';
import { getUnrecoverableValidationErrors } from '../utils/get-unrecoverable-validation-errors.js';
import '../models/line-item/prescription-glasses.js';
import { getFailureCodes, mapCartError } from '../utils/get-failure-error-codes.js';

const useGetCart = ({ cartCount , cartHash , cartId , onUnrecoverableErrors , onUpdateCartLocaleState , onFinalisedCartError , onCartDataLoadedSuccessfully ,  })=>{
    const settings = useCartSettings();
    const intl = useIntl();
    const { apiUrl  } = useApiConfig();
    const { isClearCartOnAllErrorsEnabled  } = useFeatureToggles();
    const handleTrackErrorTaelium = (errorDetails, errorMessage)=>dispatchTrackErrorEvent(()=>Promise.resolve(swcHelpers.objectSpread({
                Error_Source: 'Server',
                Error_Code: 'Get cart error',
                Error_Details: errorDetails
            }, errorMessage ? {
                Error_Message: errorMessage
            } : {}))
        )
    ;
    const handleGetCartCompleted = React.useCallback((data)=>{
        var ref, ref1, ref2;
        const failures = data === null || data === void 0 ? void 0 : (ref = data.cart) === null || ref === void 0 ? void 0 : ref.failures;
        const validationErrors = data === null || data === void 0 ? void 0 : (ref1 = data.cart) === null || ref1 === void 0 ? void 0 : ref1.validationErrors;
        const isFinalizedState = (data === null || data === void 0 ? void 0 : (ref2 = data.cart) === null || ref2 === void 0 ? void 0 : ref2.cart) && data.cart.cart.checkoutState == CheckoutState.Finalized;
        if (getUnrecoverableValidationErrors(validationErrors).length || (failures === null || failures === void 0 ? void 0 : failures.length)) {
            toast.failed('cart.errors.failedToLoadCart');
            if (failures) {
                getFailureCodes(failures).forEach((failureCode)=>{
                    console.error('cart error', failureCode);
                });
            }
            onUnrecoverableErrors === null || onUnrecoverableErrors === void 0 ? void 0 : onUnrecoverableErrors();
        } else if (isFinalizedState) {
            toast.failed('cart.errors.failedToLoadCart');
            onFinalisedCartError === null || onFinalisedCartError === void 0 ? void 0 : onFinalisedCartError();
        } else {
            onCartDataLoadedSuccessfully();
        }
    }, [
        onUnrecoverableErrors,
        onFinalisedCartError,
        onCartDataLoadedSuccessfully
    ]);
    const [getCart, { called , loading , data: data1 , error: error1 , refetch  }] = useLazyQuery(GET_CART, {
        onCompleted: handleGetCartCompleted,
        onError: (error)=>{
            console.error(error);
            handleTrackErrorTaelium(error.name, error.message);
            if (isClearCartOnAllErrorsEnabled) {
                onUnrecoverableErrors === null || onUnrecoverableErrors === void 0 ? void 0 : onUnrecoverableErrors();
            }
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        context: {
            forwardHeaders: {
                'cart-hash': cartHash
            }
        }
    });
    const refetchCallback = React.useCallback(()=>{
        if (cartId) {
            // Refetch is undefined if the initial run has not been executed
            refetch ? refetch({
                id: cartId
            }) : getCart({
                variables: {
                    id: cartId
                }
            });
        }
    }, [
        getCart,
        refetch,
        cartId
    ]);
    React.useEffect(()=>{
        if (cartId) {
            getCart({
                variables: {
                    id: cartId
                }
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        cartId
    ]);
    const fetchByCartId = React.useCallback((cartIdParam, cartHashParam)=>{
        onUpdateCartLocaleState === null || onUpdateCartLocaleState === void 0 ? void 0 : onUpdateCartLocaleState({
            cartId: cartIdParam,
            cartHash: cartHashParam || cartHash,
            cartCount
        });
        // update react context with latest cartId & carthash
        (settings === null || settings === void 0 ? void 0 : settings.setContext) && settings.setContext(swcHelpers.objectSpread({
            cartId: cartIdParam
        }, cartHashParam && {
            cartHash: cartHashParam
        }));
        // fetch cart based on latest cartId & carthash without relying on localstorage or context
        if (cartIdParam) {
            refetch ? refetch({
                id: cartIdParam
            }) : getCart(swcHelpers.objectSpread({
                variables: {
                    id: cartIdParam
                }
            }, cartHashParam && {
                context: {
                    forwardHeaders: {
                        'cart-hash': cartHashParam
                    }
                }
            }));
        }
    }, [
        cartCount,
        cartHash,
        getCart,
        onUpdateCartLocaleState,
        refetch,
        settings
    ]);
    const mappedCart = React.useMemo(()=>{
        var ref, ref3;
        if (!cartId || data1 == null) return null;
        if (!data1 || !(data1 === null || data1 === void 0 ? void 0 : data1.cart)) return null;
        if (isNil(data1 === null || data1 === void 0 ? void 0 : data1.cart.cart)) {
            return null;
        }
        const isFinalizedState = (data1 === null || data1 === void 0 ? void 0 : (ref = data1.cart) === null || ref === void 0 ? void 0 : ref.cart) && data1.cart.cart.checkoutState == CheckoutState.Finalized;
        if (isFinalizedState) return null;
        return toCartView(data1 === null || data1 === void 0 ? void 0 : data1.cart.cart, data1 === null || data1 === void 0 ? void 0 : (ref3 = data1.cart) === null || ref3 === void 0 ? void 0 : ref3.validationErrors, settings, intl, apiUrl);
    }, [
        data1,
        intl,
        settings,
        apiUrl,
        cartId
    ]);
    return React.useMemo(()=>{
        var ref;
        return {
            called,
            loading,
            data: mappedCart,
            error: !!(data1 === null || data1 === void 0 ? void 0 : (ref = data1.cart) === null || ref === void 0 ? void 0 : ref.failures) ? mapCartError(data1 === null || data1 === void 0 ? void 0 : data1.cart.failures) : error1,
            refetch: refetchCallback,
            fetchByCartId
        };
    }, [
        called,
        mappedCart,
        data1,
        error1,
        fetchByCartId,
        loading,
        refetchCallback
    ]);
};

export { useGetCart };
//# sourceMappingURL=use-get-cart.js.map
